/**
 * Dictado de la consulta → historia clínica: motivo de consulta, enfermedad actual, antecedentes personales
 * y familiares, alergias, medicación, hábitos, signos vitales y examen (extraoral, intraoral, ATM, periodonto, higiene).
 * Lo que no es anamnesis ni examen (los hallazgos por pieza) queda en `resto` para el odontograma.
 * Funciona con o sin puntuación: el dictado por voz suele llegar sin puntos.
 */
import { norm } from "./texto.ts";

export const CAMPOS_ANAMNESIS = ["motivo", "enfermedad_actual", "antecedentes_personales", "antecedentes_familiares", "alergias", "medicacion", "habitos"] as const;
export const CAMPOS_EXAMEN = ["signos_vitales", "extraoral", "intraoral", "atm", "periodontal", "higiene"] as const;
export type CampoAnamnesis = (typeof CAMPOS_ANAMNESIS)[number];
export type CampoExamen = (typeof CAMPOS_EXAMEN)[number];
export const NOMBRE_CAMPO: Readonly<Record<CampoAnamnesis | CampoExamen, string>> = {
  motivo: "Motivo de consulta", enfermedad_actual: "Enfermedad actual", antecedentes_personales: "Antecedentes patológicos personales",
  antecedentes_familiares: "Antecedentes patológicos familiares", alergias: "Alergias", medicacion: "Medicación actual", habitos: "Hábitos",
  signos_vitales: "Signos vitales", extraoral: "Examen extraoral", intraoral: "Examen intraoral", atm: "ATM", periodontal: "Periodonto", higiene: "Higiene oral",
};

export interface AnamnesisDictada {
  anamnesis: Partial<Record<CampoAnamnesis, string>>;
  examen: Partial<Record<CampoExamen, string>>;
  resto: string;   // frases para el odontograma
}

/* Encabezados que el odontólogo puede decir en voz alta ("antecedentes familiares: …") */
const ETIQUETAS: [RegExp, CampoAnamnesis | CampoExamen][] = [
  [/^motivo( de (la )?consulta)?\b/, "motivo"],
  [/^(enfermedad|problema) actual\b/, "enfermedad_actual"],
  [/^antecedentes? (patologicos? )?familiares\b/, "antecedentes_familiares"],
  [/^antecedentes? (patologicos? )?(personales)?\b/, "antecedentes_personales"],
  [/^alergias?\b/, "alergias"],
  [/^(medicacion|medicamentos?|farmacos)( actual(es)?)?\b/, "medicacion"],
  [/^habitos?\b/, "habitos"],
  [/^signos vitales\b/, "signos_vitales"],
  [/^(examen )?extra ?oral\b/, "extraoral"],
  [/^(examen )?intra ?oral\b/, "intraoral"],
  [/^(atm|articulacion temporomandibular)\b/, "atm"],
  [/^(periodonto|periodontal|examen periodontal)\b/, "periodontal"],
  [/^higiene( oral| bucal)?\b/, "higiene"],
];

/*
 * Frases que abren un dato nuevo cuando el dictado llega sin puntos. Solo se corta en estas (no en palabras
 * sueltas como "diente" o "hace") para no partir frases por la mitad.
 */
const INICIOS = [
  "motivo de (la )?consulta", "enfermedad actual", "problema actual", "el dolor", "la molestia",
  "(?<!niega |sin |no refiere |no tiene )antecedentes( patologicos)?( personales| familiares)?",
  "(?<!niega |sin |no refiere |no tiene |refiere |es )alergias", "(?:niega|no refiere|no tiene) (?:alergias|antecedentes)", "refiere alergia", "es alergic[oa]",
  "medicacion( actual)?", "habitos", "(?<!se |no )toma", "se toma", "esta tomando", "no toma", "(?<!no )fuma", "no fuma", "signos vitales", "presion arterial", "tension arterial",
  "su (madre|padre|mama|papa|hermano|hermana|abuelo|abuela|hijo|hija)", "sus (padres|abuelos|hermanos)",
  "examen (extra|intra) ?oral", "extra ?oral", "intra ?oral", "al examen", "a la exploracion", "higiene( oral)?", "se cepilla",
  "(?<!(?:madre|padre|mama|papa|hermano|hermana|abuelo|abuela|hijo|hija|tio|tia|esposo|esposa) )(?:es|era) (?:hipertens|diabetic|asmatic|epileptic|cardiopat)[oa]", "(?:presion|tension)(?= \\d)", "lengua", "paladar", "carrillos?", "labios?", "ganglios?", "piso de (?:la )?boca", "orofaringe", "caries", "pieza", "gingivitis", "periodontitis", "resto sin patologia",
];
const RE_INICIO = new RegExp(`(?<=\\s)(?:${INICIOS.join("|")})\\b`, "g");

const RE_ODONTO_INICIO = /^(caries|pieza)/;
const RE_ODONTO_PREVIO = /\b(pieza|diente|molar|premolar|caries|resina|amalgama|corona|[1-8][1-8])\b/;

function dividir(texto: string): string[] {
  const base = texto.replace(/\s+/g, " ").trim();
  // 1) puntuación (el punto entre números, como 36.8, no corta)
  const frases = base.split(/(?<!\d)[.;](?!\d)|[.;](?=\s)|\n+|,\s+/).map((x) => x.trim()).filter(Boolean);
  const out: string[] = [];
  for (const f of frases) {
    const n = norm(f);
    if (n.split(" ").length < 7) { out.push(f); continue; }
    // 2) frase larga sin puntos: se corta donde empieza otro dato
    // los hallazgos de una misma pieza no se separan: "la pieza 36 presenta caries oclusal" es un solo dato
    const cortes = [0];
    for (const m of n.matchAll(RE_INICIO)) {
      const previo = n.slice(cortes[cortes.length - 1], m.index);
      if (RE_ODONTO_INICIO.test(m[0]) && (RE_ODONTO_PREVIO.test(previo) || /^(y |e )?(en )?(la|el|las|los)?$/.test(previo.trim()))) continue;
      cortes.push(m.index!);
    }
    for (let i = 0; i < cortes.length; i++) { const t = f.slice(cortes[i], cortes[i + 1] ?? f.length).trim(); if (t) out.push(t); }
  }
  // "alergias niega …": la negación pertenece al encabezado anterior
  for (let i = 0; i < out.length - 1; i++) {
    const et = ETIQUETAS.find(([re]) => re.test(norm(out[i])));
    const m = norm(out[i + 1]).match(/^(niega|no refiere|no tiene|ninguna?)\b\s*/);
    if (et && !sinEtiqueta(out[i], et[0]) && m) { out[i] += ` ${out[i + 1].slice(0, m[0].length).trim()}`; out[i + 1] = out[i + 1].slice(m[0].length); }
  }
  // un fragmento de una sola palabra se une al anterior
  return out.filter(Boolean).reduce<string[]>((a, p) => { if (a.length && p.split(" ").length === 1 && !ETIQUETAS.some(([re]) => re.test(norm(p)))) a[a.length - 1] += ` ${p}`; else a.push(p); return a; }, []);
}

const RE = {
  motivo: /\b(acude|viene|asiste|consulta|motivo|quiere|desea|me duele|le duele|duele|dolor|molestia|se (le )?(rompio|partio|cayo|fracturo)|revision|control|chequeo|limpieza|blanquea|mal aliento|sangran|sangrado|hinchazon|inflamacion|sensibilidad|estetica|ortodoncia|brackets|protesis)\b/,
  sintoma: /\b(desde hace|hace (\d+|un|una|dos|tres|cuatro|cinco|seis|siete|ocho|diez|quince|veinte)|dolor|duele|intensidad|sobre 10|de 10|\/10|pulsatil|punzante|espontaneo|provocado|al frio|al calor|al masticar|no (le|la|lo|me) deja dormir|irradia|inflamaci|hinchaz|sangrad|sensibilidad|empeora|mejora|se automedica|ha tomado|calma)/,
  familiar: /\b(madre|padre|mama|papa|herman[oa]s?|abuel[oa]s?|tio|tia|familiar(es)?|hij[oa]s?)\b/,
  alergia: /\balergi|alergic/,
  medicacion: /\b(toma|tomando|se toma|medicad[oa]|en tratamiento con|recibe|usa (insulina|inhalador)|\d+ ?(mg|miligramos|ui|mcg)|metformina|losartan|enalapril|insulina|levotiroxina|warfarina|aspirina|clopidogrel|anticonceptiv|omeprazol|atorvastatina)/,
  personal: /\b(diabet|hipertens|asma|cardiopat|infarto|arritmi|marcapaso|epilep|convulsi|hepatitis|vih|cancer|quimioterapia|radioterapia|tiroid|renal|dialisis|gastritis|ulcera|reflujo|embaraz|lactan|anticoagul|coagulaci|hemofil|operad[oa]|cirugia|hospitaliz|artritis|osteopor|bifosfonat|anemia|tuberculosis|covid|fiebre reumatica|niega antecedentes|sin antecedentes|aparentemente sano|no refiere antecedentes)/,
  habito: /\b(fuma|fumador|cigarr|tabaco|vapea|alcohol|bebe|bruxis|rechina|aprieta los dientes|muerde|onicofag|u[ñn]as|succion|chupa el dedo|chupon|respira(cion)? (bucal|por la boca)|deglucion atipica|cafe)/,
  vitales: /\b(presion|tension arterial|pulso|frecuencia (cardiaca|respiratoria)|temperatura|saturacion|spo2|peso|talla|imc)/,
  higiene: /\b(cepill|hilo dental|seda dental|enjuague|colutorio|higiene|placa bacteriana|indice de placa)/,
  atm: /\b(atm|articulacion|temporomandibular|chasquido|click|crepit|apertura (maxima|bucal|limitada|de \d)|desviacion|trismus|bloqueo)/,
  periodontal: /\b(encias?|gingiv|periodont|bolsas?|sondaje|movilidad|recesion|furca|calculo|sarro|sangrado al sondaje)/,
  extraoral: /\b(labios?|comisura|asimetri|ganglios?|cara|facial|piel|perfil|musculos|masetero|cuello|tercio (inferior|medio))/,
  intraoral: /\b(lengua|paladar|carrillos?|mucosa|piso de (la )?boca|frenillo|torus|orofaringe|amigdala|glandulas? salival|saliva|halitosis)/,
  piezas: /\b(pieza|diente|molar|premolar|canino|incisivo|caries|resina|amalgama|corona|endodoncia|extraccion|ausente|obturad|fractura de la pieza|[1-8][1-8]|gingivitis|periodontitis|absceso|pulpitis|necrosis|fistula)\b/,
};

const limpiar = (s: string) => s.replace(/^(y|e|ademas|tambien)\s+/i, "").replace(/^\s*[a-záéíóúñ ]{0,40}?:\s*/i, (m) => (ETIQUETAS.some(([re]) => re.test(norm(m.replace(":", "")).trim())) ? "" : m)).trim();
const sinEtiqueta = (s: string, re: RegExp) => { const n = norm(s); const m = n.match(re); return m ? s.slice(m[0].length).replace(/^[\s:,-]+/, "").replace(/^(es|son|de|del paciente)\s+/i, "").trim() : s; };
const mayus = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const punto = (s: string) => (/[.!?]$/.test(s) ? s : s + ".");

export function interpretarAnamnesis(texto: string): AnamnesisDictada {
  const acum: Partial<Record<CampoAnamnesis | CampoExamen, string[]>> = {};
  const add = (k: CampoAnamnesis | CampoExamen, s: string) => { const t = s.trim(); if (t) (acum[k] ??= []).push(mayus(t)); };
  const resto: string[] = [];
  let actual: CampoAnamnesis | CampoExamen | null = null; // último encabezado dicho: lo que sigue le pertenece hasta otro dato

  for (const bruto of dividir(texto)) {
    const s = limpiar(bruto), c = norm(s);
    if (!c) continue;
    const etiqueta = ETIQUETAS.find(([re]) => re.test(c));
    if (etiqueta) {
      let contenido = sinEtiqueta(s, etiqueta[0]);
      // "alergias: niega" → "Niega alergias"
      if (/^(niega|no|ninguna?|no refiere|sin)$/i.test(norm(contenido))) contenido = `Niega ${NOMBRE_CAMPO[etiqueta[1]].toLowerCase()}`;
      actual = etiqueta[1];
      if (contenido) add(etiqueta[1], contenido);
      continue;
    }
    const campos: (CampoAnamnesis | CampoExamen)[] = [];
    if (RE.alergia.test(c)) campos.push("alergias");
    else if (RE.familiar.test(c) && (RE.personal.test(c) || /\b(murio|fallecio|padece|sufre|tiene|es)\b/.test(c))) campos.push("antecedentes_familiares");
    else {
      if (RE.personal.test(c)) campos.push("antecedentes_personales");
      if (RE.medicacion.test(c) && !/\bno toma\b/.test(c)) campos.push("medicacion");
      if (RE.habito.test(c)) campos.push("habitos");
      if (RE.vitales.test(c) && /\d/.test(c)) campos.push("signos_vitales");
      if (RE.higiene.test(c)) campos.push("higiene");
      if (RE.atm.test(c)) campos.push("atm");
      if (RE.periodontal.test(c) && !RE.piezas.test(c)) campos.push("periodontal");
      if (RE.extraoral.test(c) && !RE.piezas.test(c)) campos.push("extraoral");
      if (RE.intraoral.test(c) && !RE.piezas.test(c)) campos.push("intraoral");
    }
    const esSintoma = RE.sintoma.test(c) && !/\bal examen\b|\bpresenta caries\b/.test(c);
    // una frase con piezas o con símbolos del odontograma (prótesis de una arcada, movilidad…) no es el motivo, salvo que hable de dolor
    const esOdonto = /\b[1-8][1-8]\b|protesis (fija|removible|total)|\bpuente\b|movilidad|recesion|sellante|agenesia/.test(c) && !/duele|dolor|molest/.test(c);
    if (!campos.length && !acum.motivo && RE.motivo.test(c) && !esOdonto) {
      // el motivo va en palabras del paciente, sin "acude porque"
      add("motivo", s.replace(/^\s*(el |la )?(paciente )?(acude|viene|asiste)( a (la )?consulta)?\s+(porque|por|para|a)\s+/i, "").replace(/^(refiere|manifiesta|indica) que\s+/i, ""));
      actual = "motivo";
      continue;
    }
    if (!campos.length && esSintoma && !RE.piezas.test(c.replace(/\b(muela|diente)s?\b/g, ""))) { add("enfermedad_actual", s); actual = "enfermedad_actual"; continue; }
    if (campos.length) { for (const k of campos) add(k, s); actual = campos[0]; continue; }
    // sin palabra clave: continúa el último encabezado dicho, salvo que sea un hallazgo del odontograma
    if (actual && !RE.piezas.test(c) && !/resto sin patologia/.test(c)) { add(actual, s); continue; }
    if (RE.periodontal.test(c) && !/\b[1-8][1-8]\b|pieza/.test(c)) add("periodontal", s); // "gingivitis generalizada" también va al examen
    resto.push(s);
  }
  // si dijo síntomas pero no dijo cuál era el motivo, el primer síntoma es el motivo
  if (!acum.motivo && acum.enfermedad_actual?.length) acum.motivo = [acum.enfermedad_actual[0]];
  const unir = (l?: string[]) => (l?.length ? [...new Set(l)].map(punto).join(" ") : undefined);
  const anamnesis: AnamnesisDictada["anamnesis"] = {}, examen: AnamnesisDictada["examen"] = {};
  for (const k of CAMPOS_ANAMNESIS) { const v = unir(acum[k]); if (v) anamnesis[k] = v; }
  for (const k of CAMPOS_EXAMEN) { const v = unir(acum[k]); if (v) examen[k] = v; }
  // el examen intraoral y periodontal también puede tener hallazgos del odontograma (gingivitis, cálculo)
  const vistas = new Set<string>();
  const paraOdontograma = [...resto, ...(acum.periodontal ?? []), ...(acum.intraoral ?? [])]
    .filter((x) => { const k = norm(x).replace(/[.\s]+$/, ""); return vistas.has(k) ? false : (vistas.add(k), true); }).join(". ");
  return { anamnesis, examen, resto: paraOdontograma };
}

/** Une lo dictado con lo que ya tenía la historia: no borra nada y no repite frases ya escritas. */
export function combinarCampos(actual: Record<string, unknown>, nuevo: Record<string, string | undefined>): Record<string, string> {
  const out: Record<string, string> = Object.fromEntries(Object.entries(actual).filter(([, v]) => typeof v === "string")) as Record<string, string>;
  for (const [k, v] of Object.entries(nuevo)) {
    if (!v?.trim()) continue;
    const previo = (out[k] ?? "").trim();
    if (!previo) out[k] = v.trim();
    else if (!norm(previo).includes(norm(v.trim()).replace(/\.$/, ""))) out[k] = `${previo}${/[.!?]$/.test(previo) ? "" : "."} ${v.trim()}`;
  }
  return out;
}
